import { PayloadAction } from '@reduxjs/toolkit'
import axios, { AxiosResponse, CancelTokenSource } from 'axios'
import {
  ExtendedQueryTypeEnum,
  IBoardContact,
  IOtherContact,
  IResidentContact,
} from 'Modules/Communication/Contacts/shared/models/Contacts'
import {
  mapBoardContactsToRecipient,
  mapBoardMembersToRecipient,
  mapMeterOrObjectToRecipient,
  mapOtherContactsToRecipient,
  mapRecipientQueryToBoardContactFilters,
  mapRecipientQueryToContactFilters,
  mapRecipientQueryToOtherContactFilters,
  mapResidentContactToRecipient,
} from 'Modules/Communication/Contacts/shared/utils/recipients-mappers'
import { updateEmailRecipients } from 'Modules/Communication/Email/shared/api/emails.api'
import {
  getFrequentRecipients,
  getRecipeintsObjectTypes,
  getRecipientsMeterContacts,
  getRecipientsMeterTypes,
  getRecipientsObjectContacts,
} from 'Modules/Communication/shared/api/inbox.api'
import {
  IMessageRecipient,
  RecipientTypeEnum,
} from 'Modules/Communication/shared/models/Inbox'
import {
  FilterTypeEnum,
  IBuildingTypes,
  IMeterItemModel,
  IObjectItemModel,
  RecipientOpenState,
  IRecipientModel,
  MessageTypeEnum,
  IFrequentRecipientsModel,
} from 'Modules/Communication/shared/models/Recipients'
import { updateSmsRecipients } from 'Modules/Communication/Sms/shared/api/sms.api'
import { getCommittees } from 'Modules/HousingAssociation/BoardAndCommittees/shared/api/committee.api'
import { ICommittee } from 'Modules/HousingAssociation/BoardAndCommittees/shared/models/Committees'
import { getBuildingTypes } from 'Modules/HousingAssociation/CompanyInfo/shared/api/clientBuildings.api'
import { getClientAddresses } from 'Modules/HousingAssociation/OurCompany/shared/api/housingunit.api'
import { put, takeEvery, select, call, all } from 'redux-saga/effects'
import { getBoardMembers } from 'shared/api/board.api'
import {
  getBoardContacts,
  getOtherContacts,
  getResidentContacts,
} from 'shared/api/client.api'
import { FIND_RECIPIENTS_MODAL } from 'shared/constants/modals'
import { IBoard, IBoardMember } from 'shared/models/Board'
import { IClient } from 'shared/models/Client'
import { ICommonListModel, IPatchModel } from 'shared/models/Common'
import { IAddress } from 'shared/models/Unit'
import { getBoard } from 'shared/store/board/selectors'
import { getActiveClient } from 'shared/store/clients/selectors'
import {
  fetchRecipientsRequest,
  fetchRecipientsSuccess,
  fetchEmailRecipientsError,
  handleRecipientModal,
  resetListedRecipients,
  handleSubmitEmailRecipients,
  fetchRecipientsClientAddressesRequest,
  fetchRecipientsClientAddressesSuccess,
  fetchRecipientsClientAddressesError,
  fetchRecipientsBuildingTypesRequest,
  fetchRecipientsBuildingTypesSuccess,
  fetchRecipientsBuildingTypesError,
  fetchRecipientsCommitteesRequest,
  fetchRecipientsCommitteesSuccess,
  fetchRecipientsCommitteesError,
  setRecipientsFilters,
  fetchFrequentEmailRecipientsRequest,
  fetchFrequentSmsRecipientsRequest,
  fetchFrequentSmsRecipientsSuccess,
  fetchFrequentSmsRecipientsError,
  fetchFrequentEmailRecipientsSuccess,
  fetchFrequentEmailRecipientsError,
  handleSubmitSmsRecipients,
  fetchRecipientsObjectAndMetersTypesRequest,
  fetchRecipientsObjectAndMetersTypesSuccess,
  fetchRecipientsObjectAndMetersTypesError,
  onRecipientsClose,
} from '.'
import {
  SEND_EMAIL_REMOVE_RECIPIENT_SUCCESS,
  SEND_EMAIL_UPDATE_MANY_RECIPIENTS_REQUESTED,
} from '../email/actionTypes'
import { getSendEmail } from '../email/selectors'
import { ISendEmailState, ISendSmsState } from '../shared/SendMessage'
import {
  SEND_SMS_REMOVE_RECIPIENT_SUCCESS,
  SEND_SMS_UPDATE_MANY_RECIPIENTS_REQUESTED,
} from '../sms/actionTypes'
import { getSendSms } from '../sms/selectors'
import {
  getMessageRecipients,
  getRecipientOpenState,
  RecipientState,
} from './selectors'
import { closeModal, openModal } from 'shared/store/modal'

function* fetchRecipients(action: PayloadAction<CancelTokenSource>) {
  try {
    const client: IClient = yield select(getActiveClient)
    const { data: boardData }: { data: IBoard } = yield select(getBoard)
    const { filters, meters, objects }: RecipientState =
      yield select(getMessageRecipients)

    const recipientOpenStateType: RecipientOpenState = yield select(
      getRecipientOpenState
    )

    const queryType =
      recipientOpenStateType === 'SMS'
        ? ExtendedQueryTypeEnum.SmsContacts
        : ExtendedQueryTypeEnum.EmailContacts
    /**
     * Handling Owners & Tenants
     */
    if (
      filters.filterType === FilterTypeEnum.Owners ||
      filters.filterType === FilterTypeEnum.Tenants ||
      filters.filterType === FilterTypeEnum.Cohabitant
    ) {
      const filter = mapRecipientQueryToContactFilters(filters, queryType)

      const response: AxiosResponse<ICommonListModel<IResidentContact>> =
        yield call(getResidentContacts, client.id, filter, action.payload)

      const mapped = mapResidentContactToRecipient(
        response.data,
        client.clientType.companyTypeCode
      )
      yield put(fetchRecipientsSuccess(mapped))
    } else if (filters.filterType === FilterTypeEnum.BoardMembers) {
      const isSpecificBoard =
        filters.boardId !== 'all' && filters.boardId?.length

      const boardMembers: AxiosResponse<IBoardMember[]> = yield call(
        getBoardMembers,
        isSpecificBoard ? String(filters.boardId) : boardData.id
      )

      const mapped = mapBoardMembersToRecipient(boardMembers.data)
      yield put(fetchRecipientsSuccess(mapped))
      /**
       * Handling meters (Måler)
       */
    } else if (filters.filterType === FilterTypeEnum.Meters) {
      if (!filters.meterTypeNo || !meters.length) {
        yield put(
          fetchRecipientsSuccess({
            currentPage: 1,
            morePages: false,
            result: [],
            resultCount: 0,
            totalCount: 0,
            totalPageCount: 0,
          })
        )

        return
      }

      const result: AxiosResponse<ICommonListModel<IRecipientModel>> =
        yield call(getRecipientsMeterContacts, client.id, filters)

      const mapped = mapMeterOrObjectToRecipient(result.data)
      yield put(fetchRecipientsSuccess(mapped))

      /**
       * Handling objects (Annet)
       */
    } else if (filters.filterType === FilterTypeEnum.Objects) {
      if (!filters.objectTypeNo || !objects.length) {
        yield put(
          fetchRecipientsSuccess({
            currentPage: 1,
            morePages: false,
            result: [],
            resultCount: 0,
            totalCount: 0,
            totalPageCount: 0,
          })
        )
        return
      }

      const result: AxiosResponse<ICommonListModel<IRecipientModel>> =
        yield call(getRecipientsObjectContacts, client.id, filters)
      const mapped = mapMeterOrObjectToRecipient(result.data)
      yield put(fetchRecipientsSuccess(mapped))
      /**
       * Handling board contacts
       */
    } else if (filters.filterType === FilterTypeEnum.BoardContacts) {
      const updatedFilters = mapRecipientQueryToBoardContactFilters(filters)

      const boardContacts: AxiosResponse<ICommonListModel<IBoardContact>> =
        yield call(getBoardContacts, client.id, updatedFilters, action.payload)

      const mapped = mapBoardContactsToRecipient(boardContacts.data)
      yield put(fetchRecipientsSuccess(mapped))
    } else if (filters.filterType === FilterTypeEnum.OtherContacts) {
      const updatedFilters = mapRecipientQueryToOtherContactFilters(filters)

      const otherContacts: AxiosResponse<ICommonListModel<IOtherContact>> =
        yield call(getOtherContacts, client.id, updatedFilters, action.payload)
      const mapped = mapOtherContactsToRecipient(otherContacts.data)
      yield put(fetchRecipientsSuccess(mapped))
    }
  } catch (e: any) {
    if (!axios.isCancel(e)) {
      console.log(e.response.data)
      yield put(fetchEmailRecipientsError(e))
    }
  }
}

function* handleModal(
  action: PayloadAction<{
    open: RecipientOpenState
    type: RecipientTypeEnum | null
  }>
) {
  let payload: string = ''

  if (action.payload.open === null) {
    yield put(closeModal())
    return
  }

  switch (action.payload.open) {
    case 'Email':
    case 'SMS':
    case 'Message':
      payload = FIND_RECIPIENTS_MODAL
      break

    default:
      break
  }

  yield put(openModal({ type: payload }))
}

function* submitEmailRecipients(
  action: PayloadAction<{ overrideRecipientType?: RecipientTypeEnum }>
) {
  try {
    const {
      listedRecipients,
      openStateType,
      listedRecipientsRemovalQueue,
    }: RecipientState = yield select(getMessageRecipients)

    const recipientType = action.payload.overrideRecipientType || openStateType

    const { messageId }: ISendEmailState = yield select(getSendEmail)

    if (listedRecipientsRemovalQueue.length && messageId) {
      const updateObj = {
        op: 'remove',
        path: '/recipients',
        value: listedRecipientsRemovalQueue.map(({ nameId, email }) => ({
          id: nameId,
          email,
          recipientType: recipientType,
        })),
      } as IPatchModel

      yield call(updateEmailRecipients, messageId, [updateObj])

      yield all(
        listedRecipientsRemovalQueue.map((item) =>
          put({
            type: SEND_EMAIL_REMOVE_RECIPIENT_SUCCESS,
            payload: {
              id: item.nameId,
              email: item.email ?? undefined,
              recipientType: recipientType,
            },
          })
        )
      )
    }

    const mapped: Partial<IMessageRecipient>[] = listedRecipients.map(
      (recipient: IRecipientModel) => ({
        email: recipient.email,
        name: recipient.name.trim() || recipient.companyName?.trim() || '',
        phonenumber: recipient.phonenumber,
        nameregisterId: recipient.nameId,
        recipientType: recipientType || RecipientTypeEnum.To,
      })
    )

    yield put({
      type: SEND_EMAIL_UPDATE_MANY_RECIPIENTS_REQUESTED,
      payload: mapped,
    })

    yield put(resetListedRecipients())
    yield put(handleRecipientModal({ open: null, type: null }))
    yield put(onRecipientsClose())
  } catch (e) {
    console.error(e)
  }
}

function* submitSmsRecipients() {
  try {
    const {
      listedRecipients,
      openStateType,
      listedRecipientsRemovalQueue,
    }: RecipientState = yield select(getMessageRecipients)

    const { message }: ISendSmsState = yield select(getSendSms)

    if (listedRecipientsRemovalQueue.length) {
      const updateObj = {
        op: 'remove',
        path: '/recipients',
        value: listedRecipientsRemovalQueue.map(({ nameId, phonenumber }) => ({
          id: nameId,
          phonenumber,
          recipientType: openStateType,
        })),
      } as IPatchModel

      if (!message?.id) throw Error('Message id is not defined')

      yield call(updateSmsRecipients, message.id, [updateObj])

      yield all(
        listedRecipientsRemovalQueue.map((item) =>
          put({
            type: SEND_SMS_REMOVE_RECIPIENT_SUCCESS,
            payload: {
              id: item.nameId,
              phonenumber: item.phonenumber ?? undefined,
              recipientType: openStateType,
            },
          })
        )
      )
    }

    const mapped: Partial<IMessageRecipient>[] = listedRecipients.map(
      (recipient: IRecipientModel) => ({
        email: recipient.email,
        name: recipient.name.trim() || recipient.companyName?.trim() || '',
        phonenumber: recipient.phonenumber,
        nameregisterId: recipient.nameId,
        recipientType: openStateType || RecipientTypeEnum.To,
      })
    )

    yield put({
      type: SEND_SMS_UPDATE_MANY_RECIPIENTS_REQUESTED,
      payload: mapped,
    })

    yield put(resetListedRecipients())
    yield put(handleRecipientModal({ open: null, type: null }))
    yield put(onRecipientsClose())
  } catch (e) {
    console.error(e)
  }
}

function* fetchClientAddresses() {
  try {
    const client: IClient = yield select(getActiveClient)
    const response: AxiosResponse<IAddress[]> = yield call(
      getClientAddresses,
      client.id
    )

    yield put(fetchRecipientsClientAddressesSuccess(response.data))
  } catch (e) {
    console.log(e)
    yield put(fetchRecipientsClientAddressesError(e))
  }
}

function* fetchBuildingTypes() {
  try {
    const client: IClient = yield select(getActiveClient)
    const response: AxiosResponse<IBuildingTypes[]> = yield call(
      getBuildingTypes,
      client.id
    )

    const buildingTypes: IBuildingTypes[] = [
      { id: null, type: 'Alle byggningstyper' },
      ...response.data,
    ]

    yield put(fetchRecipientsBuildingTypesSuccess(buildingTypes))
  } catch (e) {
    console.log(e)
    yield put(fetchRecipientsBuildingTypesError(e))
  }
}

function* fetchCommittees() {
  try {
    const client: IClient = yield select(getActiveClient)
    const response: AxiosResponse<ICommittee[]> = yield call(
      getCommittees,
      client.id
    )

    const emptyCommittee: ICommittee = {
      id: 'all',
      name: 'Styret',
      clientId: client.id,
      clientNo: 0,
      mail: '',
      created: new Date(),
      members: [],
      terminates: new Date(),
      officeAddress: {
        city: '',
        country: '',
        countryCode: '',
        line1: '',
        line2: '',
        line3: '',
        zip: '',
      },
    }

    const buildingTypes: ICommittee[] = [emptyCommittee, ...response.data]

    yield put(fetchRecipientsCommitteesSuccess(buildingTypes))
    yield put(setRecipientsFilters({ boardId: 'all' }))
  } catch (e) {
    console.log(e)
    yield put(fetchRecipientsCommitteesError(e))
  }
}

function* fetchFrequentSmsRecipients() {
  try {
    const client: IClient = yield select(getActiveClient)
    const response: AxiosResponse<IFrequentRecipientsModel[]> = yield call(
      getFrequentRecipients,
      client.id,
      MessageTypeEnum.SMS
    )

    yield put(
      fetchFrequentSmsRecipientsSuccess({
        data: response.data,
        clientId: client.id,
      })
    )
  } catch (e) {
    console.log(e)
    yield put(fetchFrequentSmsRecipientsError(e))
  }
}

function* fetchFrequentEmailRecipients() {
  try {
    const client: IClient = yield select(getActiveClient)
    const response: AxiosResponse<IFrequentRecipientsModel[]> = yield call(
      getFrequentRecipients,
      client.id,
      MessageTypeEnum.Email
    )

    yield put(
      fetchFrequentEmailRecipientsSuccess({
        data: response.data,
        clientId: client.id,
      })
    )
  } catch (e) {
    console.log(e)
    yield put(fetchFrequentEmailRecipientsError(e))
  }
}

function* fetchObjectAndMeterTypes() {
  try {
    const client: IClient = yield select(getActiveClient)
    const meterTypes: AxiosResponse<IMeterItemModel[]> = yield call(
      getRecipientsMeterTypes,
      client.id
    )

    const objectTypes: AxiosResponse<IObjectItemModel[]> = yield call(
      getRecipeintsObjectTypes,
      client.id
    )

    yield put(
      fetchRecipientsObjectAndMetersTypesSuccess({
        meters: meterTypes.data,
        objects: objectTypes.data,
      })
    )
  } catch (e) {
    yield put(fetchRecipientsObjectAndMetersTypesError())
  }
}

function* emailRecipientsSaga() {
  yield takeEvery(fetchRecipientsRequest.type, fetchRecipients)
  yield takeEvery(handleRecipientModal.type, handleModal)
  yield takeEvery(handleSubmitEmailRecipients.type, submitEmailRecipients)
  yield takeEvery(handleSubmitSmsRecipients.type, submitSmsRecipients)
  yield takeEvery(
    fetchRecipientsClientAddressesRequest.type,
    fetchClientAddresses
  )
  yield takeEvery(fetchRecipientsBuildingTypesRequest.type, fetchBuildingTypes)
  yield takeEvery(fetchRecipientsCommitteesRequest.type, fetchCommittees)
  yield takeEvery(
    fetchFrequentEmailRecipientsRequest.type,
    fetchFrequentEmailRecipients
  )
  yield takeEvery(
    fetchFrequentSmsRecipientsRequest.type,
    fetchFrequentSmsRecipients
  )

  yield takeEvery(
    fetchRecipientsObjectAndMetersTypesRequest.type,
    fetchObjectAndMeterTypes
  )
}

export default emailRecipientsSaga
